import signature from '@/utils/signature';
import { showFullScreenLoading, hideFullScreenLoading } from './loading';
import { directFile, getGenKey, getDirectSts } from '@/apis/common';
import { Message } from 'element-ui';

/**
 * 封装直传方法。
 * @param {Object} options - 文件对象
 * @param {string} options.name - 文件名
 * @param {Object} options.data - sts临时身份信息
 * @returns {Promise<Object>} - 包含上传文件的 URL 和文件名的对象
 */
export const directFileFn = async (options) => {
  showFullScreenLoading();
  try {
    const fileName = options.name;
    const lastDotIndex = fileName.lastIndexOf('.');
    let fileType = lastDotIndex > -1 ? fileName.substring(lastDotIndex + 1) : '';

    // 等待获取genKey直传密钥
    const {data: res} = await getGenKey();

    // 获取STS临时身份信息
    const stsItem = JSON.parse(localStorage.getItem('stsItem'));
    options.data = stsItem.data;

    // 构建URL
    const suffix = `/${res}.${fileType}`;
    options.data.url = options.data.domain + suffix;

    // 获取签名
    const signName = await signature({
      SecretId: options.data.tmpSecretId,
      SecretKey: options.data.tmpSecretKey,
      Method: 'POST',
      Pathname: suffix,
    });

    // 执行sts直传
    await directFile(options, options.data.url, {
      key: res + '.' + fileType,
      Signature: signName,
      'x-cos-security-token': options.data.sessionToken,
    })
    hideFullScreenLoading();
    return {
      url: options.data.url,
      name: fileName,
    };
  } catch (err) {
    hideFullScreenLoading();
    console.error('错误信息:', err);
    throw err;
  }
};

/**
 * 获取直传参数并存储到本地存储中。
 */
export const getDirectInfo = async () => {
  try {
    const stsInfo = await getDirectSts(); // 调用 API 获取直传参数
    stsInfo.expirationTime = Date.now() + stsInfo.duration * 1000; // 计算过期时间
    localStorage.setItem('stsItem', JSON.stringify(stsInfo)); // 将直传参数存储到本地存储
  } catch (err) {
    Message.error('获取直传参数失败:', err); // 捕获并显示错误信息
  }
};

/**
 * 生成上传失败的提示信息。
 * @param {string[]} types - 允许的文件类型数组，例如 ["image/jpeg", "image/png"]
 * @returns {string} - 生成的错误提示信息，例如 "上传只能是 JPEG,PNG 格式!"
 */
export const ErrorMessage = (types) => {
  const extensions = types
    .map((type) => {
      // 转换文件类型，提取扩展名
      const extension = type.split('/')[1];
      if (extension === 'jpeg') return 'JPG';
      return extension.toUpperCase();
    })
    .join(',');
  return `上传只能是 ${extensions} 格式!`; // 返回生成的错误提示信息
};

/**
 * 单位映射
 */
const unitMap = {
  B: 1,
  KB: 1024,
  MB: 1024 * 1024,
  GB: 1024 * 1024 * 1024,
};

/**
 * 将带有单位的文件大小字符串转换为字节数。
 * @param {string} sizeStr - 带有单位的文件大小字符串，例如 "10MB" 或 "10"
 * @returns {number | null} - 转换后的字节数，如果输入无效则返回 null
 */
export const convertToBytes = (sizeStr) => {
  if (!sizeStr) return null;

  //正则表达式匹配数字和单位
  const match = sizeStr.match(/^(\d+(\.\d+)?)([KMGT]B?)?$/i);
  if (!match) return null;

  // 提取数字和单位
  // 不使用eslint代码检查
  // eslint-disable-next-line
  const [_, number, , unit] = match;

  // 将数字部分转换为浮点数，并根据单位映射表计算字节数
  // 如果单位部分不存在，则默认为 1
  const bytes = parseFloat(number) * (unitMap[unit.toUpperCase()] || 1);

  return bytes;
};

/**
 * 将字节数转换为人类可读的文件大小格式。
 * @param {number} bytes - 文件大小，以字节为单位
 * @returns {string} - 格式化后的文件大小，例如 "10.50 MB"
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 B';

  const k = 1024; // 定义进制
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];

  // 计算单位索引
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  // 将字节数转换为对应单位，并保留两位小数
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * @param {*} file
 * @param {Array<string>} customFileType - 自定义允许上传的文件类型
 * @param {Array<string>} fileTypeMaps  - 允许上传的文件类型
 * @param {string} size - 文件大小限制
 * @returns {boolean} - 是否允许上传
 */

export const beforeUpload = (file, customFileType, fileTypeMaps, size) => {
  let typeMaps; // 可以上传的文件类型
  let isCorrectType = false;
  // 如果指定了customFileType，则仅检查该类型
  if (customFileType.length !== 0) typeMaps = customFileType;
  else typeMaps = fileTypeMaps || fileTypeMaps.default;

  // 检查允许的文件类型
  isCorrectType = typeMaps.includes(file.type);

  // 错误提示
  let errorMessage = !isCorrectType ? ErrorMessage(typeMaps) : '';

  // 检查文件大小
  const maxSize = size ? convertToBytes(size) : null;
  const isLtMaxSize = maxSize === null || file.size <= maxSize;
  if (!isLtMaxSize) errorMessage = `上传文件大小不能超过 ${formatFileSize(maxSize)}!`;

  if (errorMessage) Message.error(errorMessage);
  return isCorrectType && isLtMaxSize;
};
